/**
 * PDP action bar scroll reveal (PDPBAR-2, PRD D6).
 * One IntersectionObserver on #pb-vp-scroll-sentinel — mobile dock mirror + desktop island share it.
 */
export const ISLAND_ACTIVE_CLASS = 'pb-action-bar-island-active';
export const DOCK_MIRROR_CLASS = 'pb-vp-dock-mirror-active';
export const DESKTOP_MEDIA = '(min-width: 768px)';

/**
 * Island follows the bar's own footprint: open once the bar has scrolled off the top, never while
 * it is merely below the fold. `islandScrolledPast` defaults to the chip sentinel (mobile path).
 */
export function resolveScrollReveal({ sentinelIntersecting, islandScrolledPast, isDesktopViewport }) {
  const scrolledPast = islandScrolledPast === undefined ? !sentinelIntersecting : islandScrolledPast;
  return {
    mobileDockOpen: !isDesktopViewport && !sentinelIntersecting,
    desktopIslandOpen: !!isDesktopViewport && !!scrolledPast,
  };
}

/** Has this box left the top of the viewport entirely? */
export function isScrolledPast(entry) {
  if (!entry || !entry.boundingClientRect) return false;
  const top = entry.rootBounds ? entry.rootBounds.top : 0;
  return entry.boundingClientRect.bottom <= top;
}

/** Hold island changes this long so jitter at the reveal edge cannot strobe. */
export const ISLAND_SETTLE_MS = 120;

export function isDesktopViewport(win) {
  return !!(win && win.matchMedia && win.matchMedia(DESKTOP_MEDIA).matches);
}

export function applyScrollRevealState(doc, state, { dockEl } = {}) {
  const body = doc && doc.body;
  if (!body) return;
  body.classList.toggle(DOCK_MIRROR_CLASS, !!state.mobileDockOpen);
  body.classList.toggle(ISLAND_ACTIVE_CLASS, !!state.desktopIslandOpen);
  if (!dockEl) return;
  if (state.mobileDockOpen) {
    dockEl.classList.add('pb-vp-dock-open');
    dockEl.setAttribute('aria-hidden', 'false');
    return;
  }
  dockEl.classList.remove('pb-vp-dock-open');
  dockEl.setAttribute('aria-hidden', state.desktopIslandOpen ? 'false' : 'true');
}

function syncMobileDockHeight(win) {
  const doc = win.document;
  if (!doc || !win.matchMedia('(max-width: 767px)').matches) return;
  const wrap = doc.querySelector('.builder-trigger-container');
  if (!wrap) return;
  const h = Math.ceil(wrap.getBoundingClientRect().height);
  if (h > 0) {
    doc.documentElement.style.setProperty('--pb-mobile-dock-height', `${h}px`);
  }
}

/** Reserve the bar's footprint so nothing in the page moves when it floats. */
function measureSlot(win) {
  const doc = win.document;
  const slot = doc.querySelector('[data-pb-action-bar-slot]');
  const bar = slot && slot.querySelector('.pb-action-bar');
  if (!slot || !bar || doc.body.classList.contains(ISLAND_ACTIVE_CLASS)) return;
  const h = Math.ceil(bar.getBoundingClientRect().height);
  if (h > 0) slot.style.setProperty('--pb-bar-slot-height', `${h}px`);
}

export function initScrollReveal(win) {
  const doc = win.document;
  const sentinel = doc.getElementById('pb-vp-scroll-sentinel');
  const slot = doc.querySelector('[data-pb-action-bar-slot]');
  const dock = doc.getElementById('pb-vp-dock-strip');
  if (!sentinel || win._pbActionBarRevealIo) return;

  let lastIntersecting = true;
  let lastScrolledPast = false;
  let islandApplied = false;
  let settleTimer = null;

  measureSlot(win);

  function apply(state) {
    applyScrollRevealState(doc, state, { dockEl: dock });
    islandApplied = state.desktopIslandOpen;
    // Re-measure with the bar back in flow, so a label that rewrapped is reflected next time.
    if (!state.desktopIslandOpen) measureSlot(win);
    if (state.mobileDockOpen) {
      win.requestAnimationFrame(() => win.requestAnimationFrame(() => syncMobileDockHeight(win)));
    } else {
      syncMobileDockHeight(win);
    }
  }

  function request() {
    const state = resolveScrollReveal({
      sentinelIntersecting: lastIntersecting,
      islandScrolledPast: slot ? lastScrolledPast : undefined,
      isDesktopViewport: isDesktopViewport(win),
    });
    // Mobile dock commits immediately; only the island waits out the edge.
    if (state.desktopIslandOpen === islandApplied) {
      if (settleTimer) {
        win.clearTimeout(settleTimer);
        settleTimer = null;
      }
      apply(state);
      return;
    }
    if (settleTimer) win.clearTimeout(settleTimer);
    settleTimer = win.setTimeout(() => {
      settleTimer = null;
      apply(state);
    }, ISLAND_SETTLE_MS);
  }

  win._pbActionBarRevealIo = new win.IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (slot && entry.target === slot) {
          lastScrolledPast = isScrolledPast(entry);
          return;
        }
        lastIntersecting = entry.isIntersecting;
      });
      request();
    },
    { root: null, rootMargin: '0px 0px -12px 0px', threshold: 0 }
  );
  win._pbActionBarRevealIo.observe(sentinel);
  if (slot) win._pbActionBarRevealIo.observe(slot);

  function onViewportChange() {
    measureSlot(win);
    request();
  }
  win.addEventListener('resize', onViewportChange, { passive: true });
  if (win.visualViewport) {
    win.visualViewport.addEventListener('resize', onViewportChange, { passive: true });
  }
}

if (typeof document !== 'undefined' && document.getElementById) {
  const boot = () => initScrollReveal(typeof window !== 'undefined' ? window : globalThis);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
